import { useCallback } from 'react'
import { FieldMetaState, useField } from 'react-final-form'
import { ISelect, Option, Select } from '@cargomart/ui'
import { FieldValidator } from 'final-form'
import isEqual from 'lodash/isEqual'
import { OPTIONS_SERVICE_TYPE } from 'modules/order/components/OrderGuestCreateForm/constants'
import { isRequiredError } from 'shared/helpers/error'
import { checkFieldError } from 'shared/helpers/form'
import { defaultGetOptionId, defaultGetOptionName } from 'shared/ui/selects/helpers/defaultValues'

interface IProps<Option, Value> extends Omit<ISelect<Option>, 'children'> {
  name: string
  calculateError?: (meta: FieldMetaState<Value | void>) => boolean | string | undefined
  className?: string
  getOptionName?: (option: Option) => string
  validate?: FieldValidator<any>
}

export function FieldSelectServiceType<Option, Value>({
  calculateError = checkFieldError,
  className,
  name,
  validate,
  getOptionName = defaultGetOptionName,
  getOptionId = defaultGetOptionId,
  ...props
}: IProps<Option, Value>) {
  const options = OPTIONS_SERVICE_TYPE as unknown as Option[]

  const {
    input: { value, onChange },
    meta
  } = useField(name, { validate, isEqual })

  const error = calculateError(meta)

  const handleChange = useCallback(
    (option: Option | null) => {
      onChange(option ? getOptionId(option) : undefined)
    },
    [getOptionId, onChange]
  )

  const valueRender = useCallback(() => {
    const option = options.find((option) => getOptionId(option) === value)

    return option ? getOptionName(option) : undefined
  }, [getOptionId, getOptionName, options, value])

  return (
    <Select
      {...props}
      className={className}
      // @ts-ignore
      error={Boolean(isRequiredError(error))}
      name={name}
      onChange={handleChange}
      value={value}
      valueRender={valueRender}
    >
      {options.map((option) => (
        <Option {...option} key={getOptionId(option)} title={getOptionName(option)} value={option} />
      ))}
    </Select>
  )
}
